import React, { useState } from 'react';
import { View, Text, TextInput, StyleSheet, ScrollView, KeyboardAvoidingView, Platform, TouchableOpacity } from 'react-native';
import { useRouter, Stack } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api } from '../../src/api/client';
import { useToast } from '../../src/components/Toast';
import Input from '../../src/components/Input';
import Button from '../../src/components/Button';
import ContentContainer from '../../src/components/ContentContainer';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { rs, fs } from '../../src/utils/responsive';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

interface Hours {
  [key: string]: { open: string; close: string };
}

/** AddLocationScreen - form to create a new car wash location. */
export default function AddLocationScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [phone, setPhone] = useState('');
  const [description, setDescription] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [hours, setHours] = useState<Hours>({});
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const toast = useToast();

  const updateHour = (day: string, field: 'open' | 'close', value: string) => {
    setHours(prev => ({
      ...prev,
      [day]: { ...prev[day], open: prev[day]?.open || '06:00', close: prev[day]?.close || '22:00', [field]: value },
    }));
  };

  const toggleDay = (day: string) => {
    setHours(prev => {
      if (prev[day]) {
        const { [day]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [day]: { open: '06:00', close: '22:00' } };
    });
  };

  const handleCreate = async () => {
    if (!name || !address || !city || !state || !zip) {
      toast.show({ type: 'error', title: 'Please fill in all required fields' });
      return;
    }
    setLoading(true);
    try {
      await api('/api/locations', {
        method: 'POST',
        body: {
          name, address, city, state, zip,
          companyName: companyName || undefined,
          phone: phone || undefined,
          supportEmail: supportEmail || undefined,
          description: description || undefined,
          hours: Object.keys(hours).length > 0 ? hours : undefined,
        },
      });
      router.replace('/owner' as never);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Stack.Screen options={{ title: 'Add Location', headerBackTitle: 'Back' }} />
      <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
          <ContentContainer>
          <Text style={styles.section}>Location Details</Text>
          <Input label="Location Name *" placeholder="Sparkle Car Wash" value={name} onChangeText={setName} icon="business-outline" />
          <Input label="Company Name" placeholder="Sparkle Wash LLC" value={companyName} onChangeText={setCompanyName} icon="briefcase-outline" />
          <Input label="Street Address *" placeholder="123 Main Street" value={address} onChangeText={setAddress} icon="location-outline" />
          <Input label="City *" placeholder="Springfield" value={city} onChangeText={setCity} />
          <View style={styles.row}>
            <View style={styles.flex1}>
              <Input label="State *" placeholder="IL" value={state} onChangeText={setState} autoCapitalize="characters" maxLength={2} />
            </View>
            <View style={styles.flex1}>
              <Input label="ZIP *" placeholder="62701" value={zip} onChangeText={setZip} keyboardType="number-pad" maxLength={10} />
            </View>
          </View>
          <Input label="Phone" placeholder="555-123-4567" value={phone} onChangeText={setPhone} icon="call-outline" keyboardType="phone-pad" />
          <Input label="Support Email" placeholder="reports@yourbusiness.com" value={supportEmail} onChangeText={setSupportEmail} icon="mail-outline" keyboardType="email-address" autoCapitalize="none" />
          <Text style={{ fontSize: fs(12), color: colors.textSecondary, marginTop: -spacing.sm, marginBottom: spacing.sm }}>
            Customer problem reports sent here. Falls back to your account email if blank.
          </Text>
          <Input label="Description" placeholder="Tell customers about your wash..." value={description} onChangeText={setDescription} icon="document-text-outline" multiline numberOfLines={3} maxLength={500} />

          <Text style={styles.section}>Operating Hours</Text>
          <TouchableOpacity style={styles.quickOption} onPress={() => {
            const allDays: Hours = {};
            DAYS.forEach(d => { allDays[d] = { open: '00:00', close: '23:59' }; });
            setHours(allDays);
          }}>
            <Ionicons name="time" size={18} color={colors.primary} />
            <Text style={styles.quickOptionText}>Set 24/7</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.quickOption} onPress={() => {
            const typical: Hours = {};
            DAYS.forEach(d => { typical[d] = { open: '06:00', close: '22:00' }; });
            setHours(typical);
          }}>
            <Ionicons name="sunny" size={18} color={colors.primary} />
            <Text style={styles.quickOptionText}>Typical Hours</Text>
          </TouchableOpacity>

          {DAYS.map(day => (
            <View key={day} style={styles.hoursRow}>
              <TouchableOpacity style={styles.dayToggle} onPress={() => toggleDay(day)}>
                <View style={[styles.checkbox, hours[day] && styles.checkboxActive]}>
                  {hours[day] && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.dayLabel}>{day.slice(0, 3).charAt(0).toUpperCase() + day.slice(1, 3)}</Text>
              </TouchableOpacity>
              {hours[day] ? (
                <View style={styles.hoursInputRow}>
                  <TextInput placeholder="06:00" placeholderTextColor={colors.textLight} value={hours[day]?.open || ''} onChangeText={v => updateHour(day, 'open', v)} style={styles.hoursInput} maxLength={5} />
                  <Text style={styles.hoursDash}>–</Text>
                  <TextInput placeholder="22:00" placeholderTextColor={colors.textLight} value={hours[day]?.close || ''} onChangeText={v => updateHour(day, 'close', v)} style={styles.hoursInput} maxLength={5} />
                </View>
              ) : (
                <Text style={styles.closedText}>Closed</Text>
              )}
            </View>
          ))}

          <Button title="Create Location" onPress={handleCreate} loading={loading} size="lg" style={styles.createBtn} />
          <Button title="Cancel" onPress={() => router.back()} variant="outline" size="md" style={styles.cancelBtn} />
          </ContentContainer>
        </ScrollView>
      </KeyboardAvoidingView>
    </>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  scroll: { padding: spacing.lg },
  section: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md, marginTop: spacing.md },
  row: { flexDirection: 'row', gap: spacing.sm },
  flex1: { flex: 1 },
  quickOption: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, marginBottom: spacing.xs },
  quickOptionText: { fontSize: fontSize.sm, color: c.primaryDark, fontWeight: fontWeight.medium },
  hoursRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, minHeight: 44 },
  dayToggle: { flexDirection: 'row', alignItems: 'center', gap: rs(6), width: rs(80) },
  checkbox: { width: rs(20), height: rs(20), borderRadius: 4, borderWidth: 2, borderColor: c.border, justifyContent: 'center', alignItems: 'center' },
  checkboxActive: { backgroundColor: c.primary, borderColor: c.primary },
  checkmark: { color: c.white, fontSize: fs(12), fontWeight: fontWeight.bold },
  dayLabel: { fontSize: fontSize.xs, color: c.text, fontWeight: fontWeight.medium },
  hoursInputRow: { flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1 },
  hoursInput: { flex: 1, fontSize: fontSize.sm, textAlign: 'center' as const, color: c.text, backgroundColor: c.surface, borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.md, paddingVertical: Platform.OS === 'android' ? rs(10) : rs(8), paddingHorizontal: spacing.sm, minHeight: 40 },
  hoursDash: { color: c.textLight, fontSize: fontSize.xs },
  closedText: { fontSize: fontSize.xs, color: c.textLight, fontStyle: 'italic' },
  createBtn: { marginTop: spacing.lg },
  cancelBtn: { marginTop: spacing.sm, marginBottom: spacing.xxl },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
